<script setup lang="ts">
import { usePiniaSynced } from '@proj-airi/stage-ui/libs/pinia'
import { useAuthStore } from '@proj-airi/stage-ui/stores/auth'
import { useCharacterOrchestratorStore } from '@proj-airi/stage-ui/stores/character'
import { useDisplayModelsStore } from '@proj-airi/stage-ui/stores/display-models'
import { useChatStore } from '@proj-airi/stage-ui/stores/chat'
import { useChatSessionStore } from '@proj-airi/stage-ui/stores/chat/session-store'
import { useModsServerChannelStore } from '@proj-airi/stage-ui/stores/mods/api/channel-server'
import { useContextBridgeStore } from '@proj-airi/stage-ui/stores/mods/api/context-bridge'
import { useAiriCardStore } from '@proj-airi/stage-ui/stores/modules/airi-card'
import { useConsciousnessStore } from '@proj-airi/stage-ui/stores/modules/consciousness'
import { useHearingStore } from '@proj-airi/stage-ui/stores/modules/hearing'
import { useSpeechStore } from '@proj-airi/stage-ui/stores/modules/speech'
import { useSettingsStageModel } from '@proj-airi/stage-ui/stores/settings/stage-model'
import { useVoiceStore } from '@proj-airi/stage-ui/stores/voice'
import { useSettingsAudioDevice } from '@proj-airi/stage-ui/stores/settings'
import { defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterView } from 'vue-router'

import { createHoloNativeActivationController } from './holo/holo-native-activation-controller.mjs'
import { useHoloRuntimeAuthorityStore } from './holo/holo-runtime-authority-store'

// 大型 AIRI Stage 渲染依赖仅在 ACTIVE 时异步加载；未登录/PASSIVE 不应拉起渲染器。
const WidgetStage = defineAsyncComponent({
  loader: async () => {
    const { WidgetStage } = await import('@proj-airi/stage-ui/components/scenes')
    return WidgetStage
  },
  onError(error, _retry, fail) {
    // 模块加载失败必须呈现，而非无限保留 ACTIVE 空容器。
    reportLocalStageError(error)
    fail()
  },
})

const authStore = useAuthStore()
const cardStore = useAiriCardStore()
const chatStore = useChatStore()
const chatSessionStore = useChatSessionStore()
const voiceStore = useVoiceStore()
// Mirror the native Stage Web's provider/hearing/speech store registration.
// This initializes their native settings/watchers without copying their logic.
useConsciousnessStore()
useHearingStore()
useSpeechStore()
const audioDevices = useSettingsAudioDevice()
const displayModels = useDisplayModelsStore()
const stageModel = useSettingsStageModel()
const syncedPinia = usePiniaSynced()
const serverChannelStore = useModsServerChannelStore()
const contextBridgeStore = useContextBridgeStore()
const orchestrator = useCharacterOrchestratorStore()
const authorityStore = useHoloRuntimeAuthorityStore()

const ready = ref(false)
const startupError = ref<string | null>(null)
const foreground = ref(document.visibilityState === 'visible')
const leader = ref(syncedPinia.isLeader())
const nativeExecutionReady = ref(false)
const nativeModelReady = ref(false)
let modelInitialization: Promise<void> | undefined

function initializeNativeModelWhenActive() {
  if (modelInitialization)
    return
  modelInitialization = (async () => {
    try {
      // 原生模型的 index/data/selection 仍遵循 AIRI Stage Web 既有顺序。
      await displayModels.initialize()
      await displayModels.loadDisplayModelsFromIndexedDB()
      await stageModel.initializeStageModel()
    }
    catch (error) {
      // 模型故障不阻断已有账号/Chat；渲染错误交由原生 Stage 提示。
      console.warn('[stage-holo] Native model resources unavailable:', error)
    }
    finally {
      nativeModelReady.value = true
    }
  })()
}
const localStageError = ref<string | null>(null)

const activation = createHoloNativeActivationController({
  authority: authorityStore,
  authStore,
  cardStore,
  chatStore,
  voiceStore,
  orchestrator,
  resolveSessionCharacter: sessionId => chatSessionStore.sessionMetas[sessionId]?.characterId,
  isLeader: () => syncedPinia.isLeader(),
  isForeground: () => foreground.value,
  onNativeStateChange: running => {
    nativeExecutionReady.value = running
    if (running)
      initializeNativeModelWhenActive()
  },
})

let stopAuthenticated: (() => void) | undefined
let stopLogout: (() => void) | undefined
let stopLeadership: (() => void) | undefined
let reconcileWork: Promise<void> = Promise.resolve()
// 仅在当前主窗口、当前 AIRI 账号完成原生 Provider 初始化后复用结果。
let authenticatedProviderOwner: string | null = null
let disposed = false

async function reconcileSelectedCharacter() {
  // 同一浏览器的 Follower 只投影共享状态，不再创建一个活跃 AIRI Runtime。
  if (!ready.value || !authStore.isAuthenticated || !foreground.value || !syncedPinia.isLeader()) {
    await activation.stopNative()
    return
  }

  // 原生 onAuthenticated 已登录立即回调不等待 Promise；若首次回调时尚未选出
  // Pinia Leader，Provider 默认配置可能被跳过。由可重入的 ACTIVE 协调边界
  // 等待 AIRI Card 原生 configureForAuthentication，绝不自行写入 Provider/模型。
  const owner = authStore.userId
  if (authenticatedProviderOwner !== owner) {
    await cardStore.configureForAuthentication(true)
    if (disposed || !authStore.isAuthenticated || authStore.userId !== owner
      || !foreground.value || !syncedPinia.isLeader()) {
      await activation.stopNative()
      return
    }
    authenticatedProviderOwner = owner
  }

  const selected = cardStore.activeCardId
  if (!selected) {
    await activation.stopNative()
    await authorityStore.clear('no-character')
    return
  }

  // 返回前台后，旧租约可能已释放；同一角色也必须重新通过服务端 Activation Gate。
  const observedLease = authorityStore.runtimeLease
  const accountChanged = !!observedLease && observedLease.user_id !== authStore.userId
  const holderSelectedAnotherCard = !!observedLease
    && observedLease.client_runtime_id === authorityStore.clientRuntimeId
    && observedLease.character_id !== selected
  if (authorityStore.characterId !== selected
    || (!authorityStore.isActive && (!observedLease || accountChanged || holderSelectedAnotherCard))) {
    await activation.stopNative()
    await authorityStore.openCharacter(selected)
  }
  if (!disposed)
    await activation.sync()
}

function reportLocalStageError(error: Error) {
  localStageError.value = error.message
  console.warn('[stage-holo] Native Stage renderer error:', error)
}

function queueReconcile() {
  reconcileWork = reconcileWork
    .then(reconcileSelectedCharacter)
    .catch((error) => {
      // 缺失的官方 Card 云端权威保持 fail-closed，不把本地卡升级为可运行卡。
      startupError.value = error instanceof Error ? error.message : String(error)
    })
}

function visibilityChanged() {
  foreground.value = document.visibilityState === 'visible'
  if (!foreground.value) {
    void activation.stopNative()
    if (syncedPinia.isLeader())
      void authorityStore.release('foreground-hidden')
  }
  else {
    queueReconcile()
  }
}

function pageHidden() {
  void activation.stopNative()
  if (syncedPinia.isLeader())
    void authorityStore.release('pagehide')
}

onMounted(async () => {
  // Chat 发送钩子必须先于 Stage 数据平面初始化，避免启动期间绕过租约。
  activation.start()
  document.addEventListener('visibilitychange', visibilityChanged)
  window.addEventListener('pagehide', pageHidden)

  stopLeadership = syncedPinia.onLeadershipChange((isLeader) => {
    leader.value = isLeader
    if (!isLeader) {
      authenticatedProviderOwner = null
      void activation.stopNative()
    }
    else {
      queueReconcile()
    }
  })

  try {
    await authStore.initialize()
    await cardStore.initialize()

    // 按 AIRI Stage Web 原生顺序：Card 完成初始化后才挂认证状态回调。
    stopAuthenticated = authStore.onAuthenticated(() => {
      // 恢复会话和登录后均进入同一串行协调流程，避免无等待的初始化竞态。
      authenticatedProviderOwner = null
      queueReconcile()
    })
    stopLogout = authStore.onLogout(async () => {
      authenticatedProviderOwner = null
      await activation.stopNative()
      // 复用 AIRI 原生登出清理；若已有新账号登录则不能清除新账号配置。
      if (!authStore.isAuthenticated && syncedPinia.isLeader())
        await cardStore.configureForAuthentication(false)
      if (!authStore.isAuthenticated)
        await authorityStore.clear('airi-logout')
    })
    if (!authStore.isAuthenticated && syncedPinia.isLeader())
      await cardStore.configureForAuthentication(false)

    await chatStore.initialize(syncedPinia)
    // Native Stage Web initializes audio-device selection after Chat/Card.
    // A denied/unavailable microphone must not prevent passive management UI.
    try {
      await audioDevices.initialize()
    }
    catch (error) {
      console.warn('[stage-holo] Native audio-device initialization unavailable:', error)
    }
    void serverChannelStore.initialize({ possibleEvents: ['ui:configure'] })
      .catch(error => console.error('[stage-holo] AIRI Server Channel initialization failed:', error))
    contextBridgeStore.initialize()

    // Character Orchestrator/Hearing 仅在服务端租约通过之后由 Activation Controller 创建。
    ready.value = true
    queueReconcile()
  }
  catch (error) {
    startupError.value = error instanceof Error ? error.message : String(error)
  }
})

watch(() => cardStore.activeCardId, () => queueReconcile())
watch(() => [authorityStore.isActive, authorityStore.phase, authorityStore.runtimeLease], () => {
  if (!ready.value)
    return
  if (!authorityStore.isActive || !leader.value)
    void activation.stopNative()
  else
    void activation.sync().catch(error => console.error('[stage-holo] Activation refused:', error))
})

onUnmounted(() => {
  disposed = true
  stopAuthenticated?.()
  stopLogout?.()
  stopLeadership?.()
  document.removeEventListener('visibilitychange', visibilityChanged)
  window.removeEventListener('pagehide', pageHidden)
  void activation.dispose()
  if (syncedPinia.isLeader())
    void authorityStore.release('stage-unmount')
  chatStore.dispose()
  contextBridgeStore.dispose()
})
</script>

<template>
  <div v-if="startupError" class="startup-state error">
    <strong>Fullmoonlit Holo 启动失败</strong>
    <span>{{ startupError }}</span>
  </div>
  <div v-else-if="!ready" class="startup-state">
    正在初始化 AIRI 原生运行时…
  </div>
  <RouterView v-else />
  <!-- AIRI 官方 WidgetStage 同时拥有角色模型、ACT、Voice 和 SpeechOutput。
       只有当前 ACTIVE 的本地 Leader 能挂载，避免重复 Voice Host 和 ACT 解析器。 -->
  <section v-if="nativeExecutionReady && nativeModelReady" class="native-stage-viewport" aria-label="AIRI 原生角色呈现" data-testid="holo-native-local-stage">
    <WidgetStage class="native-stage-renderer" @error="reportLocalStageError" />
    <p v-if="localStageError" class="native-stage-error" role="status">AIRI 本地角色渲染暂不可用：{{ localStageError }}</p>
  </section>
</template>
